/** The "related records" row under the tour header: the bookings count with
 *  the grids that take a tour (bookings, delivery logs, notifications,
 *  ext-id mappings), and the payload fields the page never showed — the
 *  template tour, the map link, the app deep link, the itinerary KML, the
 *  fixed start date — and the page's relation drawers (`extra`: chat channels,
 *  media, each with its count). Every other part is a value-action trigger;
 *  absent parts are skipped. */
import { Link, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { TourDetail } from '@/api/tours';
import { DetailSubtitle, ValueActions } from '@/components/ValueActions/ValueActions';

import {
  externalUrlActions,
  startDateActions,
  templateTourActions,
  tourBookingsActions,
  tourDeliveryLogsActions,
  tourExtIdsActions,
  tourNotificationsActions,
  type TourCtx,
} from '../tourLinks';

export function TourRelated({ tour, extra = [] }: { tour: TourDetail; extra?: ReactNode[] }) {
  const ctx: TourCtx = { tenantId: tour.tenant?.id };
  const total = tour.user_stats?.total;
  const bookingsLabel = typeof total === 'number' ? `${total} booking${total === 1 ? '' : 's'}` : 'Bookings';

  const parts = [
    <ValueActions key="bookings" actions={tourBookingsActions(tour.id, ctx)}>{bookingsLabel}</ValueActions>,
    <ValueActions key="logs" actions={tourDeliveryLogsActions(tour.id, ctx)}>Delivery logs</ValueActions>,
    <ValueActions key="notifications" actions={tourNotificationsActions(tour.id, ctx)}>Notifications</ValueActions>,
    <ValueActions key="extids" actions={tourExtIdsActions(tour.id, ctx)}>Ext IDs</ValueActions>,
    tour.template_tour && (
      <ValueActions key="template" actions={templateTourActions(tour.template_tour)}>
        Template:{' '}
        <Link component={RouterLink} to={`/tours/${tour.template_tour.id}`} underline="hover">
          {tour.template_tour.name ?? `#${tour.template_tour.id}`}
        </Link>
      </ValueActions>
    ),
    tour.map_link && (
      <ValueActions key="map" actions={externalUrlActions(tour.map_link, 'Open map')}>Map</ValueActions>
    ),
    tour.itinerary?.branchio_link && (
      <ValueActions key="app" actions={externalUrlActions(tour.itinerary.branchio_link, 'Open app link')}>
        App link
      </ValueActions>
    ),
    tour.itinerary?.kml_file && (
      <ValueActions key="kml" actions={externalUrlActions(tour.itinerary.kml_file, 'Open KML')}>KML</ValueActions>
    ),
    tour.start_date && (
      <ValueActions key="start" actions={startDateActions(tour.start_date, tour.id, ctx)}>
        Starts {tour.start_date}
      </ValueActions>
    ),
    ...extra,
  ];

  return (
    <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: -1.5, mb: 2 }}>
      <DetailSubtitle parts={parts} />
    </Typography>
  );
}
